import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import axios from 'axios';
import endpoints, { getBackendUrl } from '../../../endpoints';
import { useSelector } from 'react-redux';
import withAlert from '../../../hoc/withAlerts';
import { tokens } from '../../../theme';

const MATCH_TIMEOUT_MS = 300000
const POLL_MS = 2000

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

function stageLabel(stage) {
    switch (stage) {
        case 'loading_profiles':
            return 'Loading visible freelancer profiles...'
        case 'extracting':
            return 'Reading the job description and extracting required skills...'
        case 'scoring':
            return 'Scoring freelancer profiles against the job...'
        case 'finalizing':
            return 'Finalizing the recommendation...'
        default:
            return 'Matching this job against visible freelancer profiles. This can take a minute.'
    }
}

function isReadyResult(item) {
    return Boolean(item && item.status !== 'running' && item.status !== 'error' && item.result?.profiles)
}

function percent(value) {
    if (value == null || Number.isNaN(Number(value))) {
        return 'n/a'
    }
    return Math.round(Number(value) * 100) + '%'
}

const MARK_STYLES = {
    full: {
        Icon: CheckCircleRoundedIcon,
        color: tokens.success,
        background: tokens.mintSoft,
        label: 'full match',
    },
    partial: {
        Icon: RemoveCircleRoundedIcon,
        color: tokens.warning,
        background: 'rgba(245, 159, 0, 0.12)',
        label: 'partial match',
    },
    none: {
        Icon: CancelRoundedIcon,
        color: tokens.error,
        background: 'rgba(240, 62, 62, 0.10)',
        label: 'no match',
    },
}

function markStyle(mark) {
    return MARK_STYLES[mark] || MARK_STYLES.none
}

function sourceLabel(source) {
    if (source === 'main_skills') {
        return 'main skills'
    }
    if (source === 'portfolio') {
        return 'portfolio'
    }
    if (source === 'work_history') {
        return 'work history'
    }
    return 'none'
}

function verdictColor(verdict) {
    if (verdict === 'strong') {
        return 'success'
    }
    if (verdict === 'moderate') {
        return 'warning'
    }
    return 'default'
}

function MatchMark(props) {
    const style = markStyle(props.mark)
    const Icon = style.Icon
    const title = [style.label, props.detail, props.extra].filter(Boolean).join(' — ')
    return (
        <Tooltip arrow title={title}>
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: '6px',
                    backgroundColor: style.background,
                    color: style.color,
                    cursor: 'default',
                    verticalAlign: 'middle',
                }}
            >
                <Icon sx={{ fontSize: 16 }} />
            </Box>
        </Tooltip>
    )
}

function stickyCellSx(head) {
    return {
        position: 'sticky',
        left: 0,
        zIndex: head ? 3 : 1,
        backgroundColor: tokens.surface,
        minWidth: 180,
        fontWeight: head ? 700 : 600,
    }
}

const TAG_SOURCE_TABLES = [
    { key: 'main_skills', title: 'TAG MATCH — PROFILE SKILLS' },
    { key: 'work_history', title: 'TAG MATCH — WORK HISTORY SKILLS' },
    { key: 'portfolio', title: 'TAG MATCH — PORTFOLIO SKILLS' },
]

function TagMatchTable(props) {
    return (
        <Stack spacing={1}>
            <Divider textAlign="left">{props.title}</Divider>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={stickyCellSx(true)}>Mandatory skill</TableCell>
                            {props.profiles.map((profile) => (
                                <TableCell key={profile.id} align="center" sx={{ minWidth: 110 }}>
                                    {profile.name}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.rows.map((row) => (
                            <TableRow key={row.skill}>
                                <TableCell sx={stickyCellSx(false)}>{row.skill}</TableCell>
                                {props.profiles.map((profile) => {
                                    const cell = row.cells?.[profile.id] || row.cells?.[String(profile.id)] || {}
                                    return (
                                        <TableCell key={profile.id} align="center">
                                            <MatchMark
                                                mark={cell.mark}
                                                detail={cell.source ? sourceLabel(cell.source) : null}
                                                extra={cell.note}
                                            />
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    )
}

function ProfileMatchModal(props) {
    const [item, setItem] = useState(null)
    const [loading, setLoading] = useState(false)
    const [assigning, setAssigning] = useState(false)
    const [error, setError] = useState('')
    const [progress, setProgress] = useState(null)
    const token = useSelector((state) => state.auth.token)
    const cancelledRef = useRef(false)

    const result = item?.result?.profiles ? item.result : null
    const profiles = result?.profiles || []
    const hasSourceScores = Boolean(result?.tag_tables)
    const weights = result?.score_weights || null
    const weightLabel = (key) => weights?.[key] != null ? ` (${Math.round(weights[key] * 100)}%)` : ''

    const matchUri = () => getBackendUrl(endpoints.feedcontents_profile_match, token).replace('{id}', props.data?.id)

    // Claims the job for the current user (sets Owner) the moment Match is opened, unless it already has one.
    const claimOwner = async () => {
        if (!props.data?.id) {
            return
        }
        try {
            const uri = getBackendUrl(endpoints.feedcontents_claim_owner, token).replace('{id}', props.data.id)
            await axios.post(uri, {})
        } catch (e) {
            console.log(e)
        }
    }

    const pollUntilDone = async () => {
        const startedAt = Date.now()
        while (!cancelledRef.current && Date.now() - startedAt < MATCH_TIMEOUT_MS) {
            const res = await axios.get(matchUri())
            const next = res.data.item
            const running = Boolean(res.data.running) || next?.status === 'running'
            if (next) {
                setItem(next)
            }
            setProgress(running ? (res.data.progress || null) : null)
            if (next?.status === 'error') {
                throw new Error(next.error_text || 'Match failed')
            }
            if (isReadyResult(next) && !running) {
                return next
            }
            await sleep(POLL_MS)
        }
        if (cancelledRef.current) {
            return null
        }
        throw new Error('Match timed out. Try Re-run — the server may still finish in the background.')
    }

    const runMatch = async (force) => {
        if (!props.data?.id) {
            return
        }
        setLoading(true)
        setError('')
        setProgress(null)
        try {
            if (!force) {
                const cached = await axios.get(matchUri())
                const cachedItem = cached.data?.item
                const running = Boolean(cached.data?.running) || cachedItem?.status === 'running'
                if (isReadyResult(cachedItem) && !running) {
                    setItem(cachedItem)
                    setLoading(false)
                    return
                }
                if (running) {
                    await pollUntilDone()
                    if (!cancelledRef.current) {
                        setLoading(false)
                    }
                    return
                }
            }
            await axios.post(matchUri(), {}, { timeout: 30000 })
            await pollUntilDone()
        } catch (e) {
            if (cancelledRef.current) {
                return
            }
            const message = e.response?.data?.message || e.message || 'Match failed'
            const readable = message === 'AI_DISABLED' ? 'AI is disabled' : message
            setError(readable)
            if (props.showAlert) {
                props.showAlert(readable, 'error')
            }
        }
        if (!cancelledRef.current) {
            setLoading(false)
        }
    }

    useEffect(() => {
        cancelledRef.current = false
        if (!props.data) {
            setItem(null)
            setError('')
            setLoading(false)
            setProgress(null)
            return
        }
        runMatch(false)
        claimOwner()
        return () => {
            cancelledRef.current = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data?.id])

    const profileNote = (props.data?.notes || []).find((note) => note.name === 'profile')
    const profileOptions = (profileNote?.options || []).map((name) => String(name).trim()).filter(Boolean)
    const recommendedName = result?.recommendation?.name
    const canAssign = Boolean(
        recommendedName
        && !result?.recommendation?.weak
        && profileOptions.includes(recommendedName)
    )
    const alreadyAssigned = Boolean(recommendedName && profileNote?.value === recommendedName)

    const assignProfile = async () => {
        if (!canAssign || alreadyAssigned) {
            return
        }
        setAssigning(true)
        try {
            const uri = getBackendUrl(endpoints.feedcontents_ext_note, token).replace('{id}', props.data.id)
            await axios.put(uri, {
                name: 'profile',
                value: recommendedName,
            })
            if (props.showAlert) {
                props.showAlert('Profile assigned: ' + recommendedName, 'success')
            }
            if (props.onAssigned) {
                props.onAssigned()
            }
        } catch (e) {
            if (props.showAlert) {
                props.showAlert(e.response?.data?.message || 'Could not assign profile', 'error')
            }
        }
        setAssigning(false)
    }

    const createdAt = item?.created_at
        ? new Date(item.created_at).toLocaleString()
        : null

    return (
        <Dialog
            open={Boolean(props.data)}
            onClose={props.onClose}
            fullWidth
            maxWidth="xl"
        >
            <DialogTitle>
                Match profile
                <IconButton
                    onClick={props.onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ position: 'relative', minHeight: 220 }}>
                <Stack spacing={3} sx={{ pt: 1 }}>
                    {loading && !result ?
                        <Stack spacing={1.5} sx={{ pt: 8, alignItems: 'center' }}>
                            <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                {stageLabel(progress?.stage)}
                            </Typography>
                            {progress?.stage === 'scoring' && progress.total ?
                                <Box sx={{ width: '100%', maxWidth: 360 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.round((progress.current / progress.total) * 100)}
                                    />
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', mt: 0.5 }}>
                                        {progress.current} / {progress.total}{progress.lastProfileName ? ` — ${progress.lastProfileName}` : ''}
                                    </Typography>
                                </Box>
                                :
                                <Box sx={{ width: '100%', maxWidth: 360 }}>
                                    <LinearProgress variant="indeterminate" />
                                </Box>
                            }
                        </Stack>
                        : null}

                    {error && !result ?
                        <Alert severity="error">{error}</Alert>
                        : null}

                    {result ?
                        <>
                            <Stack spacing={1}>
                                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                                    {result.job_title || props.data?.title}
                                </Typography>
                                {createdAt ?
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                        Last run {createdAt}{item?.model ? ` · ${item.model}` : ''}
                                    </Typography>
                                    : null}
                                {result.no_explicit_skills ?
                                    <Alert severity="info">No explicit skills list is present — scoring uses the text layer only.</Alert>
                                    :
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {(result.mandatory_skills || []).map((skill) => (
                                            <Chip key={skill} size="small" label={skill} />
                                        ))}
                                    </Stack>
                                }
                            </Stack>

                            {result.recommendation ?
                                <Alert severity={result.recommendation.weak ? 'warning' : 'success'}>
                                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Recommendation</Typography>
                                    {result.recommendation.explanation}
                                    {result.recommendation.caveat ?
                                        <Typography sx={{ mt: 1 }}>{result.recommendation.caveat}</Typography>
                                        : null}
                                </Alert>
                                : null}

                            {!result.no_explicit_skills && result.tag_tables ?
                                TAG_SOURCE_TABLES.map((table) => result.tag_tables[table.key]?.length ?
                                    <TagMatchTable
                                        key={table.key}
                                        title={table.title}
                                        rows={result.tag_tables[table.key]}
                                        profiles={profiles}
                                    />
                                    : null)
                                : null}

                            {/* Results saved before per-source scoring only have the merged table. */}
                            {!result.no_explicit_skills && !result.tag_tables && result.tag_table?.length ?
                                <TagMatchTable title="TAG MATCH" rows={result.tag_table} profiles={profiles} />
                                : null}

                            {result.text_table?.length ?
                                <Stack spacing={1}>
                                    <Divider textAlign="left">{weights ? 'TEXT MATCH — EXACT PHRASES' : 'TEXT MATCH'}</Divider>
                                    <TableContainer sx={{ overflowX: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={stickyCellSx(true)}>Key concept</TableCell>
                                                    {profiles.map((profile) => (
                                                        <TableCell key={profile.id} align="center" sx={{ minWidth: 110 }}>
                                                            {profile.name}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {result.text_table.map((row) => (
                                                    <TableRow key={row.concept}>
                                                        <TableCell sx={stickyCellSx(false)}>{row.concept}</TableCell>
                                                        {profiles.map((profile) => {
                                                            const cell = row.cells?.[profile.id] || row.cells?.[String(profile.id)] || {}
                                                            return (
                                                                <TableCell key={profile.id} align="center">
                                                                    <MatchMark
                                                                        mark={cell.mark}
                                                                        extra={cell.justification}
                                                                    />
                                                                </TableCell>
                                                            )
                                                        })}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Stack>
                                : null}

                            <Stack spacing={1}>
                                <Divider textAlign="left">SUMMARY</Divider>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Profile</TableCell>
                                                {hasSourceScores ?
                                                    <>
                                                        <TableCell align="center">Profile skills{weightLabel('main_skills')}</TableCell>
                                                        <TableCell align="center">Work history{weightLabel('work_history')}</TableCell>
                                                        <TableCell align="center">Portfolio{weightLabel('portfolio')}</TableCell>
                                                    </>
                                                    : null}
                                                {!weights ? <TableCell align="center">Tag score</TableCell> : null}
                                                <TableCell align="center">Text score{weightLabel('text')}</TableCell>
                                                <TableCell align="center">Combined</TableCell>
                                                <TableCell align="center">Verdict</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(result.summary || []).map((row) => (
                                                <TableRow
                                                    key={row.roster_id}
                                                    sx={{
                                                        backgroundColor: result.recommendation?.roster_id === row.roster_id
                                                            ? tokens.accentSoft
                                                            : undefined,
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                                                        {row.title ?
                                                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{row.title}</Typography>
                                                            : null}
                                                    </TableCell>
                                                    {hasSourceScores ?
                                                        <>
                                                            <TableCell align="center">{percent(row.main_skills_score)}</TableCell>
                                                            <TableCell align="center">{percent(row.work_history_score)}</TableCell>
                                                            <TableCell align="center">{percent(row.portfolio_score)}</TableCell>
                                                        </>
                                                        : null}
                                                    {!weights ? <TableCell align="center">{percent(row.tag_score)}</TableCell> : null}
                                                    <TableCell align="center">{percent(row.text_score)}</TableCell>
                                                    <TableCell align="center">{percent(row.combined_score)}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip size="small" color={verdictColor(row.verdict)} label={row.verdict} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Stack>

                            <Stack spacing={1}>
                                <Divider textAlign="left">GAPS</Divider>
                                {(result.gaps || []).map((gap) => (
                                    <Stack key={gap.roster_id} spacing={0.5}>
                                        <Typography sx={{ fontWeight: 600 }}>{gap.name}</Typography>
                                        {gap.items?.length ?
                                            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                                {gap.items.join('; ')}
                                            </Typography>
                                            :
                                            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                                No remaining gaps on the scored skills and concepts.
                                            </Typography>
                                        }
                                    </Stack>
                                ))}
                            </Stack>

                            {result.cross_cutting ?
                                <Alert severity="info">
                                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Cross-cutting insight</Typography>
                                    {result.cross_cutting}
                                </Alert>
                                : null}
                        </>
                        : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                {canAssign ?
                    <Button onClick={assignProfile} disabled={assigning || alreadyAssigned || loading}>
                        {alreadyAssigned ? 'Profile assigned' : 'Assign profile'}
                    </Button>
                    : null}
                <Button onClick={() => { runMatch(true) }} disabled={loading}>
                    Re-run
                </Button>
                <Stack sx={{ flexGrow: 1 }} />
                <Button onClick={props.onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

export default withAlert(ProfileMatchModal)
